'use client';

import { useCollapse } from '@/uiComposites/interactive/Collapse';
import { useIsOpened } from '@/uiComposites/interactive/IsOpenedContext';
import { classes } from '@/uiPrimitives/base/classes.helpers';
import { ReactNode } from 'react';
import { meetingRowSubgrid } from './Meetings.css';
import { translateInline } from '@/globals/translate/translateInline';
import { shelf } from '@/uiPrimitives/layout/shelf';
import { occupy } from '@/uiPrimitives/layout/occupy';
import { indentSize } from '@/uiPrimitives/layout/indentSize.definitions';
import { icon } from '@/uiPrimitives/media/icon';
import { chevronIcon } from '@/uiIcons/chevronIcon.css';
import { textBlock, textStyle } from '@/uiPrimitives/typography/text';
import { addTestSelector } from '@dop/shared/helpers/testSelector';
import { unprintable } from '@/uiPrimitives/base/printable.css';

const getItemID = (id: string, index: number) => `${id}-${index}`;

const getAllItemIDs = (
	id: string,
	itemCount: number,
	alwaysShowCount: number,
) => {
	const itemIDs = [];
	for (let index = alwaysShowCount; index < itemCount; index++) {
		itemIDs.push(getItemID(id, index));
	}
	return itemIDs;
};

export const ShowMoreMeetingsLi = ({
	index,
	children,
}: {
	index: number;
	children: ReactNode;
}) => {
	const { id, isOpened } = useIsOpened();
	const { outerProps, innerProps } = useCollapse(isOpened);

	return (
		<li
			{...outerProps}
			className={classes(outerProps.className, meetingRowSubgrid)}
			id={getItemID(id, index)}
			tabIndex={-1}
		>
			<div
				{...innerProps}
				className={classes(innerProps.className, meetingRowSubgrid)}
			>
				{children}
			</div>
		</li>
	);
};

export const ShowMoreMeetingsButton = ({
	itemCount,
	alwaysShowCount,
	...props
}: Omit<
	React.ButtonHTMLAttributes<HTMLButtonElement>,
	'children' | 'onClick'
> & {
	itemCount: number;
	alwaysShowCount: number;
}) => {
	const { id, isOpened, setIsOpened } = useIsOpened();

	if (itemCount <= alwaysShowCount) {
		return null;
	}

	const itemIDs = getAllItemIDs(id, itemCount, alwaysShowCount);

	return (
		<button
			{...props}
			onClick={() => {
				if (!isOpened) {
					document.getElementById(itemIDs[0])?.focus();
				}
				setIsOpened((oldIsOpened) => !oldIsOpened);
			}}
			aria-controls={itemIDs.join(' ')}
			aria-expanded={isOpened}
			className={classes(
				props.className,
				unprintable,
				shelf({}),
				textStyle({ color: 'positiveBlue', fontWeight: 'bold' }),
			)}
			{...addTestSelector('uiShowMoreMeetingsButton')}
		>
			<span className={classes(textBlock({}))}>
				{isOpened
					? translateInline({ en: 'Less dates', nl: 'Minder datums' })
					: translateInline({
							en: `More dates (${itemIDs.length})`,
							nl: `Meer datums (${itemIDs.length})`,
						})}
			</span>
			<span
				className={classes(
					occupy({
						blockSize: 'capSize',
						inlineSize: indentSize,
						alignItems: 'end',
						justifyItems: 'end',
					}),
				)}
			>
				<i
					className={classes(
						icon({
							icon: chevronIcon,
							adjustIcon: 'rotate90deg',
							flip: isOpened ? 'horizontal' : undefined,
							blockSize: '1ex',
						}),
					)}
				/>
			</span>
		</button>
	);
};
